// Libs
import classNames from 'classnames/bind';
import { IconRefreshLine } from '@instructure/ui-icons';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Heading, Spinner, Text } from '@instructure/ui';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
// Components, Layouts, Pages
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getDetailCourse } from '~/thunks/course/courseThunk';
import Lirn from '../home/component/lirn/Lirn';
// Styles, images, icons
import styles from './CourseDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const CourseDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/course', 'common/base']);
  const dispatch = useAppDispatch();
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef<ReactPlayer>(null);
  //#endregion Declare Hook

  //#region Selector
  const coursesDetail = useAppSelector((state) => state.courseState.coursesDetail);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [played, setPlayed] = useState(0);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailCourse();
  }, []);

  //#endregion Implement Hook

  //#region Handle Function
  const handleProgress = (state: OnProgressProps) => {
    setPlayed(videoRef?.current?.getCurrentTime() ?? 0);
  };

  const handleSeek = (seconds: number) => {
    const currentTime = videoRef?.current?.getCurrentTime();
    if (currentTime && currentTime > played) {
      videoRef?.current?.seekTo(played);
    }
  };

  const handleEndedVideo = () => {
    setPlayed(0);
  };

  const handleGetDetailCourse = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    if (!courseId || !moduleId) return;

    try {
      await dispatch(getDetailCourse({ coursesId: courseId, lessonId: moduleId })).unwrap();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCourseContent = () => {
    if (isLoading) {
      return (
        <div className={cx('loadingContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('buttonRetry')}>
          <Button onClick={handleGetDetailCourse} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
            {t('retry_button_label', { ns: 'common/base' })}
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className={cx('containerCourseVideo')}>
          <ReactPlayer
            ref={videoRef}
            onProgress={handleProgress}
            onSeek={handleSeek}
            onEnded={handleEndedVideo}
            width='100%'
            height='100%'
            className={cx('videoPlayer')}
            url={coursesDetail.videoUrl}
            controls
            autoplay={true}
          />
        </div>

        <div className={cx('courseInfo')}>
          <Heading level='h3'>{coursesDetail?.moduleName}</Heading>
          <Text variant='content'>{coursesDetail?.courseName}</Text>
        </div>
      </>
    );
  };

  const renderMaterialContent = () => {
    if (isLoading) {
      return (
        <div className={cx('loadingContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('buttonRetry')}>
          <Button onClick={handleGetDetailCourse} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
            {t('retry_button_label', { ns: 'common/base' })}
          </Button>
        </div>
      );
    }

    if (!coursesDetail?.materials || coursesDetail?.materials?.length === 0) {
      return (
        <div className={cx('noDataText')}>
          <Text variant='contentSmall'>{t('course_detail_no_material_link_available')}</Text>
        </div>
      );
    }

    return (
      <>
        <Flex display='flex' direction='row' alignItems='center' justifyItems='center' width={'100%'}>
          <Heading level='h3'>{t('course_detail_material_title')}</Heading>
        </Flex>

        <div className={cx('materialContainer')}>
          {coursesDetail.materials.map((material, index) => (
            <a key={material.id || index} href={material.link} target='_blank' rel='noopener noreferrer'>
              <div className={cx('materialItem')}>
                <div className={cx('itemRow')}>
                  <img
                    src={icons.commonIconLink}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('iconLink')}
                  />
                  <div className={cx('itemLink')}>
                    <Text variant='content'>{material.link}</Text>
                  </div>
                </div>
                <hr className={cx('line')} />
              </div>
            </a>
          ))}
        </div>
      </>
    );
  };

  const handleBack = () => {
    navigate(-1);
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('course_detail_back')}</Text>
      </div>

      <div className={cx('content')}>
        <div className={cx('leftSection')}>{renderCourseContent()}</div>

        <div className={cx('rightSection')}>
          <div className={cx('rightSectionItem')}>{renderMaterialContent()}</div>

          <div className={cx('rightSectionItem', 'two')}>{<Lirn />}</div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
